'use client';
import cn from 'classnames';
import { FormikHelpers, FormikErrors } from 'formik';
import { ChangeEvent, useContext, useMemo, useState } from 'react';
import {
  CardCvcElement,
  CardExpiryElement,
  CardNumberElement,
  useElements,
  useStripe
} from '@stripe/react-stripe-js';
import { StripeCardNumberElement } from '@stripe/stripe-js';

import './UpdatePaymentForm.style.scss';

import { UserContext } from '@/components/context/UserContext/UserContext';
import Form, { BaseFormProps } from '@/components/form/lib/Form/Form';
import InputField from '@/components/form/lib/InputField/InputField';
import MaskedField from '@/components/form/lib/InputField/MaskedField';
import SubmitButton from '@/components/form/lib/SubmitButton/SubmitButton';

import CheckBox from '@/components/common/CheckBox/CheckBox';
import LoadingSpinner from '@/components/common/LoadingSpinner/LoadingSpinner';
import Select from '@/components/common/Select/Select';
import StripeElement from '@/components/common/StripeElement/StripeElement';
import Text from '@/components/common/Text/Text';

import { STRIPE_ELEMENT_OPTIONS } from '@/const/stripe';
import { attachPaymentMethod, isStripeDecline } from '@/util/stripe_client';
import { PaymentToken } from '@/types/Payment';
import { EPCCAddress } from '@/types/User';
import { US_STATES } from '@/util/user_client';
import analytics from '@/util/analytics';
import Stripe from 'stripe';

export type UpdatePaymentFormValues = {
  billingAddressSameAsShipping: boolean;
  firstName: string;
  lastName: string;
  phone: string;
  address: string;
  address2: string;
  city: string;
  postalCode: string;
  state: string;
};

interface UpdatePaymentFormProps
  extends Omit<
    BaseFormProps<UpdatePaymentFormValues>,
    'children' | 'onSubmit'
  > {
  onSubmit?: (
    values: UpdatePaymentFormValues,
    actions: FormikHelpers<UpdatePaymentFormValues>
  ) => void;
  onCancel: () => void;
  onAddressToggle?: () => void;
  addresses: EPCCAddress[];
  shouldSetNewPaymentMethodAsDefault?: boolean;
  setNewlyAttachedPaymentMethod?: (id: Stripe.PaymentMethod) => void;
}

export default function UpdatePaymentForm(props: UpdatePaymentFormProps) {
  const {
    className = '',
    initialValues,
    addresses,
    validate,
    onSubmit,
    onSubmitSuccess,
    onSubmitFailure,
    onCancel,
    onAddressToggle,
    validateOnBlur = true,
    validateOnChange = false,
    shouldSetNewPaymentMethodAsDefault = false,
    setNewlyAttachedPaymentMethod
  } = props;

  const elements = useElements();
  const stripe = useStripe();
  const [isSavingPayment, setSavingPayment] = useState<boolean>(false);
  const [cardNumberError, setCardNumberError] = useState<string | null>();
  const [cardExpiryError, setCardExpiryError] = useState<string | null>();
  const [cardCvcError, setCardCvcError] = useState<string | null>();
  const [generalError, setGeneralError] = useState<string | null>();

  const [region, setRegion] = useState<string>(initialValues.state);
  const [isBillingAddressSameAsShipping, setAddressToggle] = useState<boolean>(
    initialValues.billingAddressSameAsShipping
  );

  const billingAddress = useMemo(() => {
    // Billing changes create new records (history is kept for fraud detection),
    // so prefer the current default over demoted historical billing addresses.
    return (
      addresses.find((address) => address.isBilling && address.isDefault) ??
      addresses.find((address) => address.isBilling)
    );
  }, [addresses]);

  const shippingAddress = useMemo(() => {
    let address = addresses.find(
      (address) => !address.isBilling && address.isDefault
    );

    // If default doesn't exist, pull any billing address
    if (!address) {
      address = addresses.find((address) => !address.isBilling);
    }

    return address;
  }, [addresses]);

  const { value: user, actions: userActions } = useContext(UserContext);

  if (!user) {
    return null;
  }

  const handleStripeElementsChange = (e: any) => {
    // Set error message to be shown when the user inputs incorrect payment data
    const cardNumberType = e.elementType === 'cardNumber';
    const cardExpiryType = e.elementType === 'cardExpiry';
    const cardCvcType = e.elementType === 'cardCvc';

    if (cardNumberType && e.empty) {
      setCardNumberError('A valid credit card number is required');
    } else if (cardNumberType && typeof e.error === 'object') {
      setCardNumberError(e.error.message);
    } else if (cardNumberType && typeof e.error !== 'object') {
      setCardNumberError('');
    }

    if (cardExpiryType && e.empty) {
      setCardExpiryError('A valid expiration date is required');
    } else if (cardExpiryType && typeof e.error === 'object') {
      setCardExpiryError(e.error.message);
    } else if (cardExpiryType && typeof e.error !== 'object') {
      setCardExpiryError('');
    }

    if (cardCvcType && e.empty) {
      setCardCvcError('CVC is required');
    } else if (cardCvcType && typeof e.error === 'object') {
      setCardCvcError(e.error.message);
    } else if (cardCvcType && typeof e.error !== 'object') {
      setCardCvcError('');
    }
  };

  const handleAddressToggle = async () => {
    setAddressToggle(!isBillingAddressSameAsShipping);
    onAddressToggle?.();
  };

  const handleSubmit = async (values: UpdatePaymentFormValues) => {
    setGeneralError(null);
    const useShipping = isBillingAddressSameAsShipping && shippingAddress;

    if (!useShipping) {
      if (
        !values.firstName ||
        !values.lastName ||
        !values.address ||
        !values.city ||
        !region ||
        !values.postalCode
      ) {
        setGeneralError('Please ensure address is complete.');
        return;
      } else if (values.postalCode.length < 5) {
        setGeneralError('Please enter a valid zip code.');
        return;
      }
    }

    setSavingPayment(true);

    try {
      const card = elements?.getElement(
        'cardNumber'
      ) as StripeCardNumberElement;

      if (!card) {
        setGeneralError('There was an issue adding your payment method');
      }

      let paymentToken: PaymentToken = (await stripe?.createToken(
        card
      )) as PaymentToken;
      if (paymentToken.error) {
        setGeneralError(paymentToken.error);
      }

      const newPaymentMethod = await attachPaymentMethod(
        paymentToken.token.id,
        user.paymentCustomerId,
        false,
        shouldSetNewPaymentMethodAsDefault
      );
      setNewlyAttachedPaymentMethod?.(newPaymentMethod);
    } catch (e) {
      (isStripeDecline(e) ? console.warn : console.error)(e);
      setGeneralError(
        'There was an error updating your payment. Please double check and try again.'
      );
      setSavingPayment(false);

      analytics.track('Payment Method - Updated', {
        error: e
      });

      return;
    }

    const updateAddress = {
      type: 'address',
      address_id: billingAddress?.id,
      country: 'US',
      isDefault: true,
      isBilling: true,
      name: useShipping
        ? shippingAddress.name
        : `${values.firstName} ${values.lastName}`,
      first_name: useShipping ? shippingAddress.first_name : values.firstName,
      last_name: useShipping ? shippingAddress.last_name : values.lastName,
      phone_number: shippingAddress?.phone_number,
      line_1: useShipping ? shippingAddress.line_1 : values.address,
      line_2: useShipping ? shippingAddress.line_2 : values.address2,
      city: useShipping ? shippingAddress.city : values.city,
      postcode: useShipping ? shippingAddress.postcode : values.postalCode,
      region: useShipping ? shippingAddress.region : region
    };

    const updateAddressResponse = await fetch(`/api/user/addresses`, {
      method: 'PUT',
      body: JSON.stringify(updateAddress)
    });

    const updateSuccess = await updateAddressResponse.json();

    if (updateSuccess.success) {
      await userActions.refresh();
      onSubmitSuccess?.(values);

      analytics.track('Payment Method - Updated');
    } else {
      setGeneralError(
        'There was an error updating your billing address. Please double check and try again.'
      );

      analytics.track('Payment Method - Updated', {
        error: 'There was an error updating the user billing address'
      });
    }

    setSavingPayment(false);
  };

  const handleStateSelect = (
    event: ChangeEvent<HTMLSelectElement | HTMLInputElement>
  ) => {
    const { value } = event.currentTarget;
    setRegion(value);
  };

  return (
    <Form<UpdatePaymentFormValues>
      className={cn('UpdatePaymentForm', className)}
      initialValues={initialValues}
      validate={validate}
      // If an 'onSubmit function' is passed in, that function will override
      // this component's 'handleSubmit' function.
      onSubmit={onSubmit || handleSubmit}
      validateOnBlur={validateOnBlur}
      validateOnChange={validateOnChange}
    >
      <div className='UpdatePaymentForm-header'>
        <Text variant='title-xxs-500-upper'>Edit Payment Method</Text>
        <Text
          className={cn('Link', 'link-s', 'compact', 'underline')}
          variant='text-s'
          onClick={onCancel}
        >
          Cancel
        </Text>
      </div>

      <StripeElement
        error={cardNumberError}
        label='Credit card number'
        className='UpdatePaymentForm-field-number'
      >
        <CardNumberElement
          id='cardNumber'
          options={STRIPE_ELEMENT_OPTIONS}
          onChange={handleStripeElementsChange}
        />
      </StripeElement>

      <div className='UpdatePaymentForm-ccInfo'>
        <div className='UpdatePaymentForm-expiry'>
          <StripeElement
            error={cardExpiryError}
            label={'expiration date'}
            className='UpdatePaymentForm-field-expiry'
          >
            <CardExpiryElement
              options={STRIPE_ELEMENT_OPTIONS}
              onChange={handleStripeElementsChange}
            />
          </StripeElement>
        </div>
        <StripeElement
          error={cardCvcError}
          label={'CVC'}
          className='UpdatePaymentForm-field-cvc'
        >
          <CardCvcElement
            options={STRIPE_ELEMENT_OPTIONS}
            onChange={handleStripeElementsChange}
          />
        </StripeElement>
      </div>

      <CheckBox
        label={'My billing address is the same as my shipping address'}
        onChange={handleAddressToggle}
        checked={isBillingAddressSameAsShipping}
      />

      <InputField
        type='hidden'
        name={'billingAddressSameAsShipping'}
      />

      {!isBillingAddressSameAsShipping && (
        <>
          <Text
            variant='title-xxs-500-upper'
            className='UpdatePaymentForm-addressTitle'
          >
            Edit Billing Address
          </Text>

          <InputField
            name='firstName'
            label={'First Name'}
            type='text'
          />
          <InputField
            name='lastName'
            label={'Last Name'}
            type='text'
          />
          <InputField
            type='text'
            name='address'
            label='Address'
          />
          <InputField
            name='address2'
            label={'Apt/Unit/Suite (optional)'}
            type='text'
          />

          <div className='UpdatePaymentForm-cityState'>
            <InputField
              className='UpdatePaymentForm-city'
              name='city'
              label={'City'}
              type='text'
            />
            <div className='UpdatePaymentForm-state'>
              <label className='InputField-label'>
                <Text variant='title-xxs-500-upper'>State</Text>
              </label>
              <Select
                onChange={handleStateSelect}
                value={region}
              >
                <>
                  {US_STATES.map((state) => {
                    return (
                      <option
                        value={state}
                        key={state}
                      >
                        {state}
                      </option>
                    );
                  })}
                </>
              </Select>
            </div>
          </div>

          <MaskedField
            mask={[/\d/, /\d/, /\d/, /\d/, /\d/]}
            label={'Postal Code'}
            name='postalCode'
            guide={false}
          />
        </>
      )}

      <SubmitButton
        variant='light'
        size='small'
        disabled={
          isSavingPayment ||
          !!cardCvcError ||
          !!cardExpiryError ||
          !!cardNumberError
        }
      >
        Save
        {isSavingPayment && (
          <>
            {' '}
            <LoadingSpinner size={10} />
          </>
        )}
      </SubmitButton>

      {generalError && (
        <Text className='UpdatePaymentForm-error'>{generalError}</Text>
      )}
    </Form>
  );
}
